// Client for `bloxx-data` — the D1 control plane + one Durable Object per
// workspace that replaces Xano as the store for user-owned tables.
//
// IT REUSES THE EXISTING XANO TOKEN, deliberately. bloxx-data does not issue
// credentials; it validates the bearer it is given against Xano's auth/whoami and
// resolves {id, email}. So the token this app already holds works unchanged, and
// there is no second login, no second session to expire, and no second identity
// that can disagree with the first.
//
// Phase C adds scoped third-party tokens for front ends that are not this app.
// They resolve through the same path to the same caller, so nothing here changes.

import { tokenStore } from "./authTransport";
import { consumeStartupRead } from './startupReads';
import type { Caption } from "@cuts";

// THE WIRE VOCABULARY IS NOT DEFINED HERE ANY MORE. It used to be, in parallel
// with the Worker's own copy, and the two had to be edited in step by hand with
// neither compiler able to see the other half. `@wire` is `shared/wire.ts` at the
// root of this repo, which the Worker imports too; re-exporting it from here
// keeps every existing `from "@/lib/bloxxData"` import in the app working.
export * from "@wire";
// `export *` re-exports without binding the names in this module, so everything
// this file refers to itself is imported as well.
import type {
  EmailLead,
  EmailLeadDelivery,
  EmailLeadHook,
  EmailLeadHookCreated,
  EmailLeadHookTestResult,
  EmailReplyResult,
  EmailLogEntry,
  EmailSetupInput,
  EmailSetupPreview,
  EmailSetupResult,
  EmailStatus,
  EmailTestResult,
  WorkDocument,
  WorkDocumentSummary,
  WorkFieldInput,
  LinkKind,
  LinkSummary,
  LinkUpdate,
  LinkAgents,
  MemberSetSummary,
  MemberStatus,
  MintedMemberLink,
  ColumnDef,
  ColumnType,
  ItemType,
  RollupFn,
  RowOut,
  SearchHit,
  SelectOption,
  AdCopy,
  BrandKit,
  CarouselCopy,
  MediaAsset,
  MediaAssetKind,
  MediaBrand,
  StudioEdit,
  StudioPromptEditResult,
  StudioRecording,
  StudioRecordingDetail,
  StudioSource,
  StudioCrawlSummary,
  StudioTranscript,
} from "@wire";

// The deployed host, not localhost. This defaulted to 127.0.0.1 while bloxx-data
// had no public hostname, and kept doing so after it got one — so every
// production visit to /tables reported "the data service is not reachable" and
// told the user to start a dev server. Override only to point at a local worker.
const BASE = (import.meta.env.VITE_BLOXX_DATA_URL as string | undefined) || "https://data.bloxx.page";

export class DataError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * The parsed error body, for callers that need more than the sentence. The
     * email routes use it: bloxx-mail's refusals carry the provider it would
     * replace and the nameservers still to set (see `emailRefusal`).
     */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "DataError";
  }
}

async function req<T>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    /**
     * Sent as-is instead of `body`, for an upload. A File goes up as the request
     * stream rather than as JSON or multipart — there is exactly one of them, and
     * multipart would mean buffering it to build a boundary for no gain.
     */
    rawBody?: BodyInit;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...(opts.headers ?? {}) };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await ((opts.method === undefined || opts.method === 'GET' ? consumeStartupRead(token ?? '', `${BASE}${path}`) : undefined) ?? fetch(`${BASE}${path}`, {
      method: opts.method ?? "GET",
      headers,
      body: opts.rawBody ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    }));
  } catch {
    /**
     * A `fetch` TypeError means NO RESPONSE ARRIVED, and there are two very
     * different reasons for that.
     *
     * THIS USED TO BLAME THE HOST UNCONDITIONALLY and tell the reader to run
     * `npm run dev` — advice that is nonsense against the deployed service, and
     * that sent somebody looking at their own machine while a request which had
     * genuinely reached production died part-way through. A confident wrong
     * diagnosis is worse than "Failed to fetch", because it is actionable and
     * the action is useless.
     *
     * The honest split is whether we are pointed at localhost. Against a local
     * worker "it is not running" is nearly always right. Against the deployed one
     * the service is plainly up, so the truthful statement is that THIS request
     * did not come back — and it names the request, so the next person has
     * somewhere to look.
     */
    const local = /^https?:\/\/(localhost|127\.0\.0\.1)/.test(BASE);
    throw new DataError(
      0,
      local
        ? `Could not reach the data service at ${BASE}. Start it with \`npm run dev\` in ~/dev/bloxx-data.`
        : `The data service did not answer ${opts.method ?? "GET"} ${path}. Nothing was refused — no response ` +
          `came back at all, which usually means the call was cut short rather than rejected. If it was a sync, ` +
          `the source may be too large to finish in one go; a narrower view is the fix.`,
    );
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const msg =
      data && typeof data === "object" && "error" in data && typeof (data as { error: unknown }).error === "string"
        ? (data as { error: string }).error
        : data && typeof data === "object" && "error" in data && typeof (data as {error?: {message?:unknown}}).error?.message === "string"
          ? (data as {error:{message:string}}).error.message
          : `Request failed (${res.status})`;
    throw new DataError(res.status, msg, data);
  }
  return data as T;
}

// ── Types, mirroring the worker ─────────────────────────────────────────────


/** Types whose value the row already has. Read-only everywhere. */
export const SYSTEM_TYPES: readonly ColumnType[] = ["uuid", "created_at", "updated_at", "created_by", "updated_by"];

export function isSystemType(t: ColumnType): boolean {
  return SYSTEM_TYPES.includes(t);
}

/**
 * `user:2` -> "james", `derived:drv_x` -> "a lookup", `sync` -> "a sync".
 *
 * AN ACTOR IS NOT A PERSON, and that is the whole reason this is not just
 * `personLabel`. A row can equally have been last touched by a scheduled sync, by
 * a shared link, or by an enrichment field — and rendering any of those as a blank
 * or as a raw string is how "who changed this" stops being answerable.
 */
export function actorLabel(people: readonly Person[], actor: unknown): string {
  const a = String(actor ?? "").trim();
  if (!a) return "";
  const m = /^user:(\d+)$/.exec(a);
  if (m) return personLabel(people, Number(m[1]));
  // An agent's own writes. Resolved through the directory so it reads as the name
  // somebody gave it rather than as a negative integer.
  const ag = /^agent:(-\d+)$/.exec(a);
  if (ag) {
    const label = personLabel(people, Number(ag[1]));
    return label || "an agent";
  }
  if (a.startsWith("derived:")) return "a lookup";
  if (a.startsWith("link:")) return "a shared link";
  if (a.startsWith("review:")) return "a review";
  if (a === "sync") return "a sync";
  return a;
}

/**
 * A member of the workspace, as it arrives beside the rows.
 *
 * A `person` cell holds an ACCOUNT ID and nothing else, so every surface that
 * renders one needs this to turn it into a name. It travels WITH the rows
 * precisely so nothing has to make a second call — a consumer that has to would
 * either not bother or render the id, which is what the admin grid did.
 */
export interface Person {
  user_id: number;
  email: string;
  name: string;
  /**
   * Set when this principal is an AGENT, carrying the account it belongs to.
   *
   * On the same shape as a person deliberately: a `person` column stores a user id
   * and does not care which kind it is, so everything that renders an assignee
   * renders an agent already. This is only for saying WHOSE it is.
   */
  agent_of?: number;
}

/** An agent belonging to one person. See bloxx-data migration 0015. */
export interface Agent {
  id: string;
  /** Negative, so it can never collide with a real account. */
  user_id: number;
  owner_user_id: number;
  name: string;
  /** `<owner email>+agent@…` — the address it is assigned by. */
  handle: string;
  created_at: number;
}

/**
 * What to call somebody, given the directory that came with the rows.
 *
 * The fallback matters more than the happy path. An id that is not in the
 * directory is not a bug to hide: it is somebody who was removed from the
 * workspace after being assigned, and saying so is the only thing that explains
 * why the cell will not let you keep them.
 */
export function personLabel(people: readonly Person[], id: unknown): string {
  if (id === null || id === undefined || id === "") return "";
  const n = Number(id);
  const p = people.find((x) => x.user_id === n);
  // A negative id nobody claims is an agent whose owner has left the workspace —
  // named as an agent rather than as an account, because "Account -1000002" is
  // the least explicable thing a cell could say.
  if (!p) return n < 0 ? "an agent" : `Account ${n}`;
  // An agent is stored with its handle in `email`, which is an address and not a
  // name. Its `name` is the readable one and is always set.
  if (p.agent_of) return p.name || "an agent";
  return p.name || p.email.split("@")[0] || `Account ${n}`;
}

/**
 * Row id -> the title of that row, as it arrives beside the rows.
 *
 * The `people` contract applied to references: a `ref` cell holds a row id, and
 * without this every consumer would render a uuid. A missing entry is a DANGLING
 * reference — the row it pointed at was deleted — which is a real state to show,
 * not an error.
 */
export type RefTitles = Record<string, string>;

/** Two letters for the tile. Same idea as the record view's monogram. */
export function personInitials(label: string): string {
  const parts = label.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}


/** A connected Google Calendar, as the API shows it: never any token. */
export interface CalendarConnection {
  id: string;
  label: string;
  account_email: string;
  calendar_id: string;
  scopes: string;
  last_synced_at: number;
  last_error: string;
  created_at: number;
}

/** One bot visit to one booking's call. See bloxx-data src/meetings.ts. */
export interface MeetingSession {
  session_key: string;
  table_id: string;
  row_id: string;
  page_id: string;
  change_id: number;
  status: "scheduled" | "joining" | "transcribing" | "summarizing" | "ready" | "partial" | "failed" | "cancelled" | "deleted";
  reason: string;
  meet_url: string;
  start_at: number;
  started_at: number | null;
  ended_at: number | null;
  audio_seconds: number | null;
  gaps: { fromMs: number; toMs: number; cause: string }[];
  notes_version: number;
  updated_at: number;
  created_at: number;
  title: string;
  timezone: string;
  slot_minutes: number;
  has_notes: boolean;
}

export interface MeetingSegment {
  seq: number;
  speaker: string;
  startMs: number;
  endMs: number;
  text: string;
  lowConfidence: boolean;
  retracted: boolean;
}

export interface MeetingNotes {
  notes_version: number;
  complete: boolean;
  reasons: string[];
  summary: string;
  decisions: { text: string; refs: number[] }[];
  action_items: { text: string; owner: string | null; due: string | null; refs: number[] }[];
  open_questions: { text: string; refs: number[] }[];
  model: string;
  created_at: number;
}

/** The notes settings a booking page carries (bloxx-data migration 0074). */
export interface BookingPageNotes {
  id: string;
  title: string;
  meet: "on" | "off";
  notes_enabled: boolean;
  meet_room_url: string;
}

export interface Workspace {
  id: string;
  name: string;
  owner_user_id: number;
  created_at: number;
  role?: "owner" | "editor" | "reader";
}

/** One comment on a row. `author` is an actor string like `user:2`. */
/** What a `file` cell holds. The bytes live at `/f/<key>` on the data host. */
export interface FileRef {
  key: string;
  name: string;
  size: number;
  type: string;
}

/** Where an uploaded file is served from. Public by design — see the /f/ route. */
export const fileUrl = (key: string) => `${BASE}/f/${key}`;

export interface RowComment {
  id: string;
  author: string;
  body: string;
  at: number;
  edited_at: number;
}




/** Types that carry an option list. */
export function hasOptions(t: ColumnType): boolean {
  return t === "select" || t === "multi_select";
}

/** A multi_select cell is a list; everything else is one value. Both arrive as `unknown`. */
export function cellValues(value: unknown): string[] {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  return [String(value)];
}


/** Read-only wherever it appears: a rollup is worked out, not typed. */
export function isComputed(t: ColumnType): boolean {
  return t === "rollup" || isSystemType(t);
}

/**
 * The sources a table's rows can come from, and what each one needs asked.
 *
 * ONE LIST, because three screens were about to describe the same five adapters:
 * the rail's badge, the grid's read-only notice, and the connect form. It mirrors
 * `SOURCE_KINDS` and `buildFetch` in `bloxx-data/src/sources.ts` — the fields
 * here are exactly the ones each adapter reads, so a form that passes validation
 * cannot produce a config the server then rejects for a missing key.
 *
 * `mark` is a GLYPH rather than a brand logo: shipping five logos means keeping
 * five logos current, and a wrong-looking Supabase mark is worse than a letter.
 */
export interface SourceProvider {
  kind: string;
  label: string;
  /**
   * The same thing, phrased to sit MID-SENTENCE.
   *
   * `label` is a menu entry and is capitalised like one, which read as "A live
   * mirror of An API returning JSON" the moment it was dropped into prose. Two
   * strings beats one string plus a rule about when to lowercase it, because
   * "Airtable" must stay capitalised and "an API" must not.
   */
  noun: string;
  /** What the rail says about a table that came from here. */
  badge: string;
  mark: string;
  /**
   * The brand's domain, for logo.dev, when this source IS a brand.
   *
   * Deliberately absent for `manual`, `csv` and `http_json`: those are not
   * products and giving them a logo would mean inventing one. logo.dev answers
   * 200 with a generic monogram for a domain it does not recognise, so opting in
   * per provider is what stops that appearing as if it were real.
   */
  domain?: string;
  /** Only these can be connected. `manual` is the absence of a source. */
  connectable: boolean;
  fields: { key: string; label: string; hint?: string; required?: boolean; placeholder?: string }[];
  /** What the credential is called where the person got it, so they can find it. */
  secretLabel?: string;
  secretHint?: string;
}

export const SOURCE_PROVIDERS: SourceProvider[] = [
  { kind: "manual", label: "Made here", noun: "nowhere else", badge: "Made here", mark: "◆", connectable: false, fields: [] },
  {
    kind: "csv",
    label: "A CSV at a URL",
    noun: "a CSV",
    // An UPLOADED csv and a csv at a url share this kind and are opposite
    // objects, so the badge says "imported": every table that reaches the rail
    // with `csv` and no url got there through Import.
    badge: "Imported from a CSV",
    mark: "⇪",
    connectable: true,
    fields: [
      { key: "url", label: "CSV URL", required: true, placeholder: "https://example.com/rows.csv" },
      {
        key: "external_id_field",
        label: "Which column is the id?",
        hint: "Re-syncing matches on it. Without one, every row looks new every time and every item link breaks.",
        placeholder: "sku",
      },
    ],
  },
  {
    kind: "airtable",
    label: "Airtable",
    noun: "Airtable",
    badge: "Synced from Airtable",
    mark: "A",
    domain: "airtable.com",
    connectable: true,
    secretLabel: "Personal access token",
    secretHint:
      "Create one at airtable.com/create/tokens with TWO scopes — `schema.bases:read` so Bloxx can list your bases and tables, and `data.records:read` so it can sync them — and grant it access to the bases you want.",
    fields: [
      {
        key: "base",
        label: "Base ID",
        required: true,
        placeholder: "appXXXXXXXXXXXXXX",
        hint: "The appXXXX… part of the base's URL.",
      },
      { key: "table", label: "Table name or ID", required: true, placeholder: "Companies" },
      {
        key: "view",
        label: "View (optional)",
        hint: "Recommended: it publishes one curated slice rather than the whole table, so what leaves Airtable is decided in Airtable.",
        placeholder: "Grid view",
      },
    ],
  },
  {
    kind: "sheets",
    label: "Google Sheets",
    noun: "Google Sheets",
    badge: "Synced from Google Sheets",
    mark: "S",
    domain: "docs.google.com",
    connectable: true,
    fields: [
      {
        key: "sheet",
        label: "Sheet ID",
        required: true,
        placeholder: "1AbC…",
        hint: "The long id in the sheet's URL. The sheet must be shared as “anyone with the link can view”.",
      },
      {
        key: "gid",
        label: "Tab gid",
        hint: "The gid= in the URL with that tab open. Blank means the first tab.",
        placeholder: "0",
      },
      { key: "external_id_field", label: "Which column is the id?", placeholder: "sku" },
    ],
  },
  {
    kind: "supabase",
    label: "Supabase",
    noun: "Supabase",
    badge: "Synced from Supabase",
    mark: "⌁",
    domain: "supabase.com",
    connectable: true,
    secretLabel: "API key",
    secretHint: "Settings → API. Use the anon key, and give it read access through RLS.",
    fields: [
      { key: "url", label: "Project URL", required: true, placeholder: "https://xxxx.supabase.co" },
      { key: "table", label: "Table", required: true, placeholder: "companies" },
      { key: "select", label: "Columns", hint: "A PostgREST select. Blank means all of them.", placeholder: "*" },
      { key: "filter", label: "Filter", hint: "A PostgREST query string.", placeholder: "status=eq.live" },
      { key: "external_id_field", label: "Which column is the id?", placeholder: "id" },
    ],
  },
  {
    kind: "http_json",
    label: "An API returning JSON",
    noun: "an API",
    badge: "Synced from an API",
    mark: "{}",
    connectable: true,
    secretLabel: "Bearer token",
    secretHint: "Sent as Authorization: Bearer …. Leave it blank if the endpoint is open.",
    fields: [
      { key: "url", label: "URL", required: true, placeholder: "https://api.example.com/v1/things" },
      {
        key: "items_path",
        label: "Where is the list?",
        hint: "A dotted path to the array, e.g. data.items. Blank if the body IS the array.",
        placeholder: "data.items",
      },
      { key: "external_id_field", label: "Which field is the id?", placeholder: "id" },
    ],
  },
];

export const SOURCE_BY_KIND: Record<string, SourceProvider> = Object.fromEntries(
  SOURCE_PROVIDERS.map((p) => [p.kind, p]),
);

/** A connected account. Mirrors `ConnectionOut` in bloxx-data's connections.ts. */
export interface ConnectionOut {
  id: string;
  provider: string;
  label: string;
  /** Whether a credential is stored — never the credential. */
  has_key: boolean;
  verified_at: number;
  last_error: string;
  created_at: number;
}

/** A table the connected account holds, as the provider describes it. */
export interface DiscoveredTable {
  id: string;
  name: string;
  views?: { id: string; name: string }[];
  fields?: string[];
  /**
   * Which OTHER tables this one links to, by the provider's own table id.
   *
   * Airtable already knows its relationships, and importing several tables while
   * throwing that away would produce exactly what this product exists to avoid:
   * a pile of disconnected tables holding ids of each other as text. Kept as real
   * `ref` columns when both ends come over together.
   */
  links?: { field: string; to_table_id: string }[];
  row_count?: number;
}

/** What a table's source is, as the server reports it. The secret is never in here. */
export interface SourceState {
  kind: string;
  config: Record<string, unknown>;
  /** Whether a credential is stored — never the credential itself. */
  secret_set: boolean;
  /** Set when the key comes from a connected account rather than this table. */
  connection_id?: string;
  refresh_mode: "manual" | "interval";
  refresh_interval_s: number;
  last_synced_at: number;
  last_error: string;
}

export interface TableDef {
  /** Where the rows come from: manual, csv, airtable, sheets, supabase, http_json. */
  source_kind?: string;
  /**
   * Whether a sync can still fetch it — so whether the rows are read-only.
   *
   * NOT DERIVABLE FROM `source_kind`: an uploaded CSV and a CSV at a url share a
   * kind and are opposite objects. The first is a copy that is yours to edit, the
   * second a mirror the next sync overwrites.
   */
  source_connected?: boolean;
  id: string;
  name: string;
  created_at: number;
  item_type: ItemType;
  title_column_id: string;
  /**
   * How widely the table may be READ — workspace | shared | public.
   *
   * Separate from whether it is published, deliberately: publishing makes a table
   * addressable by name, and this decides who may actually read it. A published
   * table nobody may read is the state that looks broken and is not, so both are
   * shown together rather than one implying the other.
   */
  /**
   * canonical field name -> column id. A MAPPING, NEVER A RENAME: the column
   * keeps the name the source gave it, and the table separately knows which of
   * its columns is the Address.
   */
  field_map?: Record<string, string>;
  visibility: "workspace" | "shared" | "public";
  /** Whether it appears in the registry. Discovery, not permission. */
  listed: boolean;
  columns?: ColumnDef[];
}

/** One field a template offers. Mirrors bloxx-data's `templates.ts`. */
export interface TemplateField {
  name: string;
  type: ColumnType;
  /** Whether an agent can answer anything useful without it. Never enforced on a write. */
  required: boolean;
  hint?: string;
  options?: string[];
}

export interface FieldScore {
  name: string;
  required: boolean;
  hint?: string;
  present: boolean;
  /** How many SAMPLED rows have something in it. Absent when the column is missing. */
  filled?: number;
}

export interface TableScore {
  item_type: string;
  score: number;
  rows: number;
  /** How many rows the fill half looked at. Said out loud, because it is a sample. */
  sampled: number;
  fields: FieldScore[];
  advice: string[];
}



// ── The DataSource interface ────────────────────────────────────────────────

/**
 * What the grid talks to. NOT the bloxx-data client.
 *
 * The grid is written against this and never against a transport, because the
 * second implementation is already known: feeds still live in Xano and will for
 * the whole of Phases B and C, and Phase D moves them here. A grid coupled to one
 * store would have to be rewritten to show the other, and during the migration
 * both have to work at once.
 *
 * `capabilities` is how the grid knows what to offer rather than guessing. A
 * synced feed is not editable — the next sync would overwrite the edit — so its
 * source reports `editable: false` and the grid shows a lock instead of an input.
 * An editable grid over read-only data is a lie the UI tells once and the user
 * discovers later.
 */
export interface DataSource {
  readonly capabilities: {
    editable: boolean;
    /** Whether filtering/sorting happen server-side. False means the grid must not pretend to filter a page it only partly holds. */
    serverFilter: boolean;
    maxColumns: number;
  };
  schema(): Promise<TableDef>;
  /**
   * `narrow` is the ad-hoc filter and sort a person applies while looking at the
   * table. It is deliberately NOT a saved view: no name, no visibility, no
   * `$caller` binding. A source that cannot narrow server-side ignores it, which
   * is why `capabilities.serverFilter` exists to be asked first — filtering a
   * page in the browser would quietly mean "matches among the rows you happen to
   * have loaded".
   */
  rows(
    page: { limit: number; offset: number },
    narrow?: { filters: ViewFilter[]; sort: ViewSort[] },
  ): Promise<{ rows: RowOut[]; total: number; people?: Person[]; refs?: RefTitles }>;
  insert(fields: Record<string, unknown>): Promise<RowOut>;
  update(rowId: string, fields: Record<string, unknown>): Promise<RowOut>;
  remove(rowId: string): Promise<void>;
  addColumn(name: string, type: ColumnType, extra?: NewColumnExtra): Promise<ColumnDef>;
  patchColumn(columnId: string, patch: ColumnPatch): Promise<ColumnDef>;
  dropColumn(columnId: string): Promise<void>;
}

/** What a new column needs beyond a name and a type. All optional; each type uses at most one. */
export interface NewColumnExtra {
  ref_table?: string;
  rollup?: { via_column_id: string; fn: RollupFn; value_column_id?: string };
  select_options?: SelectOption[];
}

/**
 * What can be changed about a column after it exists.
 *
 * `select_options` is a WHOLE-LIST REPLACE, not a merge: the editor holds the
 * list and sends what it should be. Ids are what preserve identity across that
 * replace — see `SelectOption`.
 */
export interface ColumnPatch {
  name?: string;
  searchable?: boolean;
  select_options?: SelectOption[];
}

/** A native table in a workspace's Durable Object. Fully editable. */
export class WorkspaceTableSource implements DataSource {
  readonly capabilities = { editable: true, serverFilter: true, maxColumns: 100 };

  constructor(
    private workspaceId: string,
    private tableId: string,
  ) {}

  private get base() {
    return `/workspaces/${this.workspaceId}/tables/${this.tableId}`;
  }

  async schema() {
    return (await req<{ table: TableDef }>(this.base)).table;
  }
  async rows(page: { limit: number; offset: number }, narrow?: { filters: ViewFilter[]; sort: ViewSort[] }) {
    const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
    // Sent only when there is something to send, so the unfiltered read stays the
    // plain `listRows` path it has always been.
    if (narrow?.filters.length) q.set("filters", JSON.stringify(narrow.filters));
    if (narrow?.sort.length) q.set("sort", JSON.stringify(narrow.sort));
    return req<{ rows: RowOut[]; total: number; people?: Person[]; refs?: RefTitles }>(`${this.base}/rows?${q}`);
  }
  async insert(fields: Record<string, unknown>) {
    return (await req<{ row: RowOut }>(`${this.base}/rows`, { method: "POST", body: { fields } })).row;
  }
  async update(rowId: string, fields: Record<string, unknown>) {
    return (await req<{ row: RowOut }>(`${this.base}/rows/${rowId}`, { method: "PATCH", body: { fields } })).row;
  }
  async remove(rowId: string) {
    await req(`${this.base}/rows/${rowId}`, { method: "DELETE" });
  }
  async addColumn(name: string, type: ColumnType, extra: NewColumnExtra = {}) {
    return (
      await req<{ column: ColumnDef }>(`${this.base}/columns`, { method: "POST", body: { name, type, ...extra } })
    ).column;
  }
  async patchColumn(columnId: string, patch: ColumnPatch) {
    return (await req<{ column: ColumnDef }>(`${this.base}/columns/${columnId}`, { method: "PATCH", body: patch }))
      .column;
  }
  async dropColumn(columnId: string) {
    await req(`${this.base}/columns/${columnId}`, { method: "DELETE" });
  }
}

// ── Views ───────────────────────────────────────────────────────────────────

export const FILTER_OPS = ["eq", "neq", "contains", "gt", "gte", "lt", "lte", "empty", "notempty"] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

/** Ops that take no value. The editor hides the value box for these rather than showing one that is ignored. */
export const VALUELESS_OPS: FilterOp[] = ["empty", "notempty"];

/**
 * The bindings a filter value may take instead of a literal.
 *
 * Presented as a CHOICE in the editor rather than a magic string somebody types.
 * `$caller.emai` is refused by the server precisely so a typo cannot silently
 * match nothing, but the better fix is not offering the chance to mistype it.
 */
export const CALLER_BINDINGS = [
  { value: "$caller.email", label: "the email of whoever is asking" },
  { value: "$caller.user_id", label: "the account id of whoever is asking" },
] as const;

export interface ViewFilter {
  field: string;
  op: FilterOp;
  value?: unknown;
}

export interface ViewSort {
  field: string;
  dir: "asc" | "desc";
}

export interface ViewColumnCfg {
  key: string;
  hidden?: boolean;
  width?: number;
  order: number;
}

export interface ViewConfig {
  columns: ViewColumnCfg[];
  filters: ViewFilter[];
  sort: ViewSort[];
}

export type ViewVisibility = "private" | "workspace" | "public";

export interface ViewRecord {
  id: string;
  workspace_id: string;
  table_id: string;
  slug: string;
  name: string;
  owner_user_id: number;
  config: string;
  visibility: ViewVisibility;
  /** DERIVED SERVER-SIDE. Sending it does nothing; it is here only to be displayed. */
  requires_caller: number;
  created_at: number;
}

export const emptyConfig = (): ViewConfig => ({ columns: [], filters: [], sort: [] });

export function parseViewConfig(v: ViewRecord): ViewConfig {
  try {
    const c = JSON.parse(v.config) as Partial<ViewConfig>;
    return { columns: c.columns ?? [], filters: c.filters ?? [], sort: c.sort ?? [] };
  } catch {
    return emptyConfig();
  }
}

export function isCallerBinding(v: unknown): boolean {
  return typeof v === "string" && v.startsWith("$caller.");
}

/**
 * Rows read THROUGH a view: filtered, sorted, and projected to the view's columns.
 *
 * Reads go through the view endpoint; writes go straight to the table. That split
 * is deliberate. A view is a lens on rows, not a different set of them, so editing
 * a cell through a filtered view must write the same row the unfiltered grid would
 * — and the view endpoint has no business accepting writes it would then have to
 * re-filter.
 */
export class ViewSource implements DataSource {
  readonly capabilities = { editable: true, serverFilter: true, maxColumns: 100 };
  private table: WorkspaceTableSource;

  constructor(
    private workspaceId: string,
    private tableId: string,
    private viewId: string,
  ) {
    this.table = new WorkspaceTableSource(workspaceId, tableId);
  }

  private get base() {
    return `/workspaces/${this.workspaceId}/tables/${this.tableId}/views/${this.viewId}`;
  }

  async schema(): Promise<TableDef> {
    const r = await req<{ table: TableDef; columns: ColumnDef[] }>(`${this.base}/rows?limit=1`);
    // The view's own column list, not the table's: a column the view hides is
    // absent from the payload, so offering it in the grid would render an empty
    // column that no amount of scrolling explains.
    return { ...r.table, columns: r.columns };
  }
  async rows(page: { limit: number; offset: number }) {
    return req<{ rows: RowOut[]; total: number; people?: Person[]; refs?: RefTitles }>(
      `${this.base}/rows?limit=${page.limit}&offset=${page.offset}`,
    );
  }
  insert(fields: Record<string, unknown>) {
    return this.table.insert(fields);
  }
  update(rowId: string, fields: Record<string, unknown>) {
    return this.table.update(rowId, fields);
  }
  remove(rowId: string) {
    return this.table.remove(rowId);
  }
  addColumn(name: string, type: ColumnType, extra?: NewColumnExtra) {
    return this.table.addColumn(name, type, extra);
  }
  patchColumn(columnId: string, patch: ColumnPatch) {
    return this.table.patchColumn(columnId, patch);
  }
  dropColumn(columnId: string) {
    return this.table.dropColumn(columnId);
  }
}

// ── Tokens ──────────────────────────────────────────────────────────────────

export interface TokenRecord {
  id: string;
  name: string;
  prefix: string;
  created_at: number;
  last_used_at: number;
  expires_at: number;
  revoked_at: number;
  /** The one workspace this key works in, or "" for every workspace you are in (migration 0069). */
  workspace_id: string;
  access: "read" | "write";
}

export interface MintTokenInput {
  name: string;
  workspace_id?: string;
  access?: "read" | "write";
  ttl_days?: number;
}

/** A table's publication: what makes it readable as a feed. */
export interface PublicationRecord {
  id: string;
  table_id: string;
  provider_id: string;
  slug: string;
  title: string;
  description: string;
  field_map: string;
  join_code: string;
  write_policy: string;
  listed: number;
  item_count: number;
  pages_enabled: number;
  archived_at: number;
}

/**
 * The canonical keys a field map can fill, per declared type.
 *
 * DECLARING A TYPE AND MAPPING ONTO IT IS THE WHOLE NETWORK PREMISE: if three
 * publishers describe events with their own column names, one question has to be
 * asked three times and merged by hand. In ws8 this was a free-text JSON box, which
 * is why so many maps went unfilled — the UI had no idea what columns existed, so
 * it could not offer them. Here it can, so it is a set of dropdowns.
 */
export const CANONICAL_FIELDS: Record<string, string[]> = {
  Event: ["name", "description", "startDate", "time", "location", "url"],
  Organization: ["name", "logo", "description", "url", "category", "cohort", "program"],
  Offer: ["name", "description", "value", "seller", "category", "url"],
  Article: ["name", "author", "description", "url", "markdown"],
  Person: ["name", "logo", "description", "url", "jobTitle"],
  Place: ["name", "description", "address", "url"],
  Product: ["name", "logo", "description", "price", "category", "url"],
  Task: ["name", "description", "status", "dueDate"],
  /**
   * The three types the pages-as-rows work added. They had no entry here, so
   * every one of them fell through to `Other` — which is why a Question showed
   * its `Order` column above its answer until the synonym list was patched.
   * Declaring them is what lets the record view draw them as what they are.
   */
  WebSite: ["name", "logo", "description", "url", "category"],
  Question: ["name", "description", "category"],
  WebPage: ["name", "description", "url", "status"],
  Other: ["name", "description", "url"],
};

/** A stored template bound to a table. Reached through a table link. */
export interface InterfaceRecord {
  id: string;
  workspace_id: string;
  table_id: string;
  slug: string;
  name: string;
  kind: string;
  created_at: number;
  updated_at: number;
}

/**
 * "When Status becomes Done, post to Slack."
 *
 * `secret` — the Slack webhook URL, or a bearer for a plain webhook — is
 * structurally absent from this shape rather than blanked, because the server
 * never selects it into a response. Whether one exists is not even reported: for
 * a Slack rule its existence is implied by the rule existing at all, since one
 * cannot be created without it.
 */
/** One thing a rule is about. `values` empty means any value. */
export interface RuleCondition {
  column_id: string;
  values: string[];
}

export interface AutomationRule {
  id: string;
  workspace_id: string;
  table_id: string;
  name: string;
  /** insert | update | delete */
  on_ops: string[];
  /** The column that must have moved. Empty means any column. LEGACY: see `conditions`. */
  column_id: string;
  /** The value it must have moved TO. Empty means any value. LEGACY: see `conditions`. */
  to_value: string;
  /**
   * What the rule is about, as several things.
   *
   * AND BETWEEN, OR WITHIN. "Done or Shipped" is one condition with two values;
   * "Done and the owner is set" is two conditions. Always populated on read — the
   * server reads an older `column_id`/`to_value` pair back as one condition, so
   * nothing here has to know which way a rule was written.
   */
  conditions: RuleCondition[];
  /** The view this rule watches, when it watches one. Rules fire on ENTERING it. */
  view_id: string;
  kind: string;
  config: { url?: string };
  /** How far through the table's change log this rule has read. */
  cursor: number;
  active: boolean;
  /** What went wrong last time. The honest surface — there is no retry. */
  last_error: string;
  last_fired_at: number;
  fires: number;
  created_at: number;
  created_by: number;
}

/** Rows referencing one row, grouped by the `ref` column doing the referencing. */
export interface BacklinkGroup {
  table_id: string;
  table: string;
  item_type: ItemType;
  column_id: string;
  column: string;
  /** The REAL count. `rows` is a page of it, so a tab badge does not lie. */
  total: number;
  rows: RowOut[];
}

/**
 * A column filled in by calling something outside.
 *
 * `secret` — the API key — is structurally absent, because the server never
 * selects it into a response. BYO key: the first version spends the customer's
 * money rather than ours.
 */
export interface DerivedField {
  id: string;
  workspace_id: string;
  table_id: string;
  name: string;
  provider: string;
  config: { url?: string; method?: string; headers?: Record<string, string>; body?: string };
  /** Column ids this reads. They are the guard AND the cost control. */
  inputs: string[];
  /** `"data.price.amount": "<column id>"` — a dotted path per destination column. */
  outputs: Record<string, string>;
  trigger_kind: string;
  cursor: number;
  active: boolean;
  /** Field-level trouble: bad config, no key. Per-row trouble lives in `state`. */
  last_error: string;
  last_run_at: number;
  /** Calls actually made. The visible half of the cost model. */
  calls: number;
  created_at: number;
  created_by: number;
  state?: {
    done: number;
    /** Rows written, but missing something the answer should have had. */
    partial: number;
    failed: number;
    notes: { row_id: string; status: string; detail: string; ran_at: number }[];
  };
}

export interface WorkspaceMember {
  user_id: number;
  role: "reader" | "editor" | "owner";
  email?: string;
  name?: string;
  created_at?: number;
}

/**
 * A URL that lets an assistant reach one workspace's tables.
 *
 * The token itself is NOT on this shape: it is returned once at mint time and
 * cannot be read back, so a listing shows the prefix and nothing more.
 */
export interface TableLink {
  id: string;
  /** Who it borrows. NEGATIVE means it carries an agent rather than the minter. */
  user_id: number;
  workspace_id: string;
  label: string;
  prefix: string;
  can_write: number;
  created_at: number;
  last_used_at: number;
  calls: number;
  revoked_at: number;
}

// ── Fixes: the readiness score as rows ──────────────────────────────────────
//
// THE WIRE SHAPE OF `src/fixes.ts`, and the reason the screen can be honest: a
// fix does not describe itself in prose, it hands over the exact list of writes
// it will make, each with the values in it and — where a value was quoted from a
// cell — which cell. Rendering that list IS the preview, so there is no second
// description to drift from what actually runs.

export const FIX_IDS = ["faq", "author", "pricing"] as const;
export type FixId = (typeof FIX_IDS)[number];

/** One cell a write will set, with the value AND how it reads to a person. */
export interface PlannedCell {
  column: string;
  value: unknown;
  /** A ref cell's value is a row id; `shown` is that row's name. Display this. */
  shown: string;
  /** `Sites · Tagline` — which cell the value was quoted from. Absent when composed. */
  from?: string;
}

export type PlannedWrite =
  | { op: "create_table"; table: string; itemType: string; columns: string[]; what: string }
  | { op: "add_column"; table: string; column: string; type: string; refTable?: string; what: string }
  | { op: "add_row"; table: string; cells: PlannedCell[]; what: string }
  | { op: "set_cell"; table: string; rowId: string; rowName: string; cells: PlannedCell[]; what: string };

export interface FixPlan {
  id: FixId;
  title: string;
  why: string;
  /** `ready` has writes; `nothing_to_do` is measured and already true; `blocked` names what is missing. */
  status: "ready" | "nothing_to_do" | "blocked";
  reason: string;
  writes: PlannedWrite[];
  /** Fingerprint of `writes`. Applying without it is refused by the server. */
  plan: string;
}

export interface FixReport {
  site: { table: string; row_id: string; name: string } | null;
  /**
   * Printed on the screen verbatim, and not paraphrased.
   *
   * It is the sentence that says these findings are a READING of this
   * workspace's own rows, as opposed to the connector's `where`, which is a
   * prescription off the templates and has seen nobody's data. The two are
   * easy to confuse and expensive to confuse, so the distinction travels with
   * the payload rather than living in a comment in one repo.
   */
  basis: string;
  fixes: FixPlan[];
}

export interface FixApplied {
  basis: string;
  applied: true;
  fix: FixId;
  plan: string;
  written: { op: PlannedWrite["op"]; table: string; what: string; id: string }[];
  note: string;
}

/** A table somebody else owns and has granted you. */
export interface SharedTable {
  table_id: string;
  workspace_id: string;
  role: string;
  name: string;
  slug: string;
  item_count: number;
  owner: string;
  owner_slug: string;
  created_at: number;
}

// ── Everything above a single table ─────────────────────────────────────────

export const bloxxData = {
  baseUrl: BASE,

  listWorkspaces: () => req<{ workspaces: Workspace[] }>("/workspaces"),

  /**
   * Tables other people have granted you.
   *
   * Its own call because /workspaces cannot answer it: a grantee is not a member,
   * so the workspace tree structurally omits exactly the half of the product that
   * makes sharing worth anything.
   */
  listShared: () => req<{ tables: SharedTable[] }>("/shared"),
  createWorkspace: (name: string) =>
    req<{ workspace: Workspace }>("/workspaces", { method: "POST", body: { name } }),

  listTables: (workspaceId: string) => req<{ tables: TableDef[] }>(`/workspaces/${workspaceId}/tables`),
  /**
   * `fields: true` creates the required fields of that item type.
   *
   * A DATABASE SHOULD NEVER START BLANK — but it is opt-in, because a table about
   * to be given its columns by a CSV would otherwise carry five it does not want.
   */
  createTable: (workspaceId: string, name: string, item_type: ItemType = "Other", fields?: true | string[]) =>
    req<{ table: TableDef }>(`/workspaces/${workspaceId}/tables`, {
      method: "POST",
      body: { name, item_type, ...(fields ? { fields } : {}) },
    }),

  /** What a table of each kind should have. Needed BEFORE a table exists. */
  templates: () => req<{ templates: Record<string, TemplateField[]> }>("/templates"),

  /**
   * Connected accounts. ONE credential per service, not one per table.
   *
   * Account-level rather than workspace-level, which is why they are not under
   * /workspaces: "the Airtable account I already connected" is a question that
   * spans workspaces, and it is answered from the control plane.
   */
  listConnections: () => req<{ connections: ConnectionOut[] }>("/connections"),
  createConnection: (provider: string, label: string, key: string, url?: string) =>
    req<{ connection: ConnectionOut }>("/connections", {
      method: "POST",
      body: { provider, label, key, ...(url ? { url } : {}) },
    }),
  /** An empty key leaves the stored one alone, the same rule as everywhere else. */
  patchConnection: (id: string, patch: { label?: string; key?: string }) =>
    req<{ connection: ConnectionOut }>(`/connections/${id}`, { method: "PATCH", body: patch }),
  deleteConnection: (id: string) => req<{ ok: true }>(`/connections/${id}`, { method: "DELETE" }),

  /**
   * WHAT THE ACCOUNT ACTUALLY HOLDS — the reason nobody should be typing
   * `appXXXXXXXXXXXXXX` into a form. We are holding a credential for a service
   * that will list its own bases and tables; asking a person to transcribe an id
   * out of somebody else's URL bar is declining to make a request we can already
   * make.
   */
  connectionBases: (id: string) => req<{ bases: { id: string; name: string }[] }>(`/connections/${id}/bases`),
  /**
   * Bring SEVERAL tables over in one call, keeping the links between them.
   *
   * Not a loop over the single-table path, and that is the whole point: an
   * Airtable link field can only become a `ref` once BOTH tables exist, so
   * importing them together is the only moment every end is known. The server
   * does it in two passes for that reason.
   *
   * Answers per table rather than all-or-nothing — one hitting a rate limit must
   * not discard the ones that worked.
   */
  connectMany: (workspaceId: string, body: { connection_id: string; base: string; tables: string[] }) =>
    req<{
      ok: boolean;
      summary: string;
      tables: { name: string; ok: boolean; rows?: number; error?: string }[];
      links: { from: string; field: string; to: string }[];
    }>(`/workspaces/${workspaceId}/connect-many`, { method: "POST", body }),

  connectionTables: (id: string, base?: string) =>
    req<{ tables: DiscoveredTable[] }>(
      `/connections/${id}/tables${base ? `?base=${encodeURIComponent(base)}` : ""}`,
    ),

  /**
   * Where a table's rows come from.
   *
   * ON THE TABLE, not behind a publication. `/feeds/source/configure` does the
   * same job but runs `ownedFeedTable` first, so a table could only be connected
   * after being published as a feed with a provider, a slug and a field map —
   * which is why the Tables screen rendered a "Synced from Airtable" badge it had
   * no way to set.
   */
  getSource: (workspaceId: string, tableId: string) =>
    req<{ source: SourceState }>(`/workspaces/${workspaceId}/tables/${tableId}/source`),

  /**
   * `secret` is WRITE-ONLY end to end: sending an empty one leaves the stored key
   * alone, because the form cannot read it back to resend it and a save that
   * blanked somebody's API key because they edited a refresh interval is
   * indistinguishable from that key being revoked. Clearing is `clear_secret`.
   */
  configureSource: (
    workspaceId: string,
    tableId: string,
    patch: {
      kind?: string;
      config?: Record<string, unknown>;
      secret?: string;
      clear_secret?: boolean;
      refresh_mode?: "manual" | "interval";
      refresh_interval_s?: number;
    },
  ) =>
    req<{ ok: true; kind: string; secret_set: boolean; refresh_mode: string; refresh_interval_s: number }>(
      `/workspaces/${workspaceId}/tables/${tableId}/source`,
      { method: "PUT", body: patch },
    ),

  /** Back to manual, credential dropped, ROWS KEPT. Without it, connecting is a one-way door. */
  disconnectSource: (workspaceId: string, tableId: string) =>
    req<{ ok: true; kind: string }>(`/workspaces/${workspaceId}/tables/${tableId}/source`, { method: "DELETE" }),

  /**
   * Pull now.
   *
   * ANSWERS 200 WITH `ok: false` AND AN `error` WHEN THE SOURCE FAILED — the call
   * worked, the fetch did not, and a caller that cannot tell those apart reports
   * somebody else's outage as a bug in Bloxx. So callers must check `ok`, not
   * merely that this resolved.
   */
  syncSource: (workspaceId: string, tableId: string) =>
    req<{ ok: boolean; error: string; added: number; updated: number; archived: number }>(
      `/workspaces/${workspaceId}/tables/${tableId}/source/sync`,
      { method: "POST", body: {} },
    ),

  /**
   * How answerable a table is, and what would make it more so.
   *
   * The advice is the part anybody uses. Nobody acts on 62; they act on "no Price
   * column, so an agent cannot answer what anything costs".
   */
  tableScore: (workspaceId: string, tableId: string) =>
    req<{ score: TableScore }>(`/workspaces/${workspaceId}/tables/${tableId}/score`),
  patchTable: (
    workspaceId: string,
    tableId: string,
    patch: {
      name?: string;
      item_type?: ItemType;
      title_column_id?: string;
      /** Merged, not replaced; an empty value clears just that one key. */
      field_map?: Record<string, string>;
      visibility?: "workspace" | "shared" | "public";
      listed?: boolean;
    },
  ) => req<{ table: TableDef }>(`/workspaces/${workspaceId}/tables/${tableId}`, { method: "PATCH", body: patch }),
  dropTable: (workspaceId: string, tableId: string) =>
    req(`/workspaces/${workspaceId}/tables/${tableId}`, { method: "DELETE" }),

  /**
   * The caller's own agent. GET reports absence rather than creating one — a read
   * that allocates a principal means anything polling it has made one.
   */
  getAgent: () => req<{ agent: Agent | null; handle: string }>("/agent"),
  createAgent: (name?: string) => req<{ agent: Agent }>("/agent", { method: "POST", body: { name } }),

  listMembers: (workspaceId: string) =>
    req<{ members: WorkspaceMember[] }>(`/workspaces/${workspaceId}/members`),
  /**
   * Share a workspace with somebody by EMAIL.
   *
   * They must have signed in to Bloxx at least once — `known_users` is what the
   * lookup resolves against — and an unknown address is refused rather than
   * granted to nobody, which is the failure you would otherwise find out about a
   * week later when they still cannot see the list.
   */
  addMember: (workspaceId: string, email: string, role: "reader" | "editor" | "owner") =>
    req<{ members: WorkspaceMember[] }>(`/workspaces/${workspaceId}/members`, {
      method: "POST",
      body: { email, role },
    }),
  removeMember: (workspaceId: string, userId: number) =>
    req(`/workspaces/${workspaceId}/members/${userId}`, { method: "DELETE" }),

  /**
   * The readiness score, turned back into the rows that move it.
   *
   * READS ONLY. `listFixes` writes nothing — a plan is a description of writes —
   * and `applyFix` REQUIRES the fingerprint of the plan that was displayed, so
   * this client cannot apply anything the person was not shown. That is a
   * property of the endpoint rather than of the screen, which is why the
   * fingerprint is carried around rather than dropped: a client that lost it
   * could not write at all. See `src/fixes.ts`.
   */
  listFixes: (workspaceId: string) => req<FixReport>(`/workspaces/${workspaceId}/fixes`),
  applyFix: (workspaceId: string, id: FixId, plan: string) =>
    req<FixApplied>(`/workspaces/${workspaceId}/fixes/${id}`, {
      method: "POST",
      body: { apply: true, plan },
    }),

  /** An assistant's way in to ONE workspace. See bloxx-data migration 0011. */
  listInterfaces: (workspaceId: string) =>
    req<{ interfaces: InterfaceRecord[] }>(`/workspaces/${workspaceId}/interfaces`),
  createBoard: (workspaceId: string, tableId: string, name: string, groupBy?: string) =>
    req<{ interface: InterfaceRecord; path: string }>(`/workspaces/${workspaceId}/interfaces`, {
      method: "POST",
      body: { table_id: tableId, name, group_by: groupBy },
    }),
  deleteInterface: (workspaceId: string, slug: string) =>
    req(`/workspaces/${workspaceId}/interfaces/${slug}`, { method: "DELETE" }),
  /**
   * A link that opens ONE interface and nothing else (bloxx-data migration 0056).
   * The old way minted a workspace-wide table link, so a board sent to a friend
   * was also an assistant key to every table beside it.
   */
  mintInterfaceLink: (workspaceId: string, slug: string, canWrite: boolean) =>
    req<{ id: string; token: string; url: string; path: string; can_write: boolean }>(
      `/workspaces/${workspaceId}/interfaces/${slug}/link`,
      { method: "POST", body: { can_write: canWrite } },
    ),

  /**
   * Member links: each member of a table edits their own row through a private
   * link. See bloxx-data src/members.ts. Links are shown ONCE, when made.
   */
  memberSchema: (workspaceId: string, tableId: string) =>
    req<{ columns: { id: string; name: string; type: string; options: string[] }[] }>(
      `/workspaces/${workspaceId}/member-sets/schema?table_id=${encodeURIComponent(tableId)}`,
    ),
  createMemberSet: (
    workspaceId: string,
    spec: { table_id: string; title: string; email_col: string; show_cols: string[]; edit_cols: string[]; agents?: LinkAgents },
  ) => req<{ set: MemberSetSummary }>(`/workspaces/${workspaceId}/member-sets`, { method: "POST", body: spec }),
  memberStatus: (workspaceId: string, setId: string) =>
    req<{ members: MemberStatus[] }>(`/workspaces/${workspaceId}/member-sets/${setId}/members`),
  mintMemberLinks: (workspaceId: string, setId: string, emails?: string[]) =>
    req<{ links: MintedMemberLink[]; skipped: string[] }>(`/workspaces/${workspaceId}/member-sets/${setId}/links`, {
      method: "POST",
      body: emails ? { emails } : {},
    }),
  revokeMemberLink: (workspaceId: string, setId: string, linkId: string) =>
    req<{ ok: true }>(`/workspaces/${workspaceId}/member-sets/${setId}/links/${linkId}`, { method: "DELETE" }),

  /** A shared list: a table and a link, in one step. See bloxx-data src/list.ts. */
  createList: (workspaceId: string, spec: { title: string; items: string[]; mode: "edit" | "view" }) =>
    req<{ list: { id: string; title: string }; table: { id: string; name: string }; url: string; mcp_url: string; items: number }>(
      `/workspaces/${workspaceId}/lists`,
      { method: "POST", body: spec },
    ),
  /**
   * Google Calendars connected to a workspace, and which ones a booking page
   * uses. See bloxx-data src/calendar.ts. `scopes` says whether a connection can
   * also add bookings to the calendar (calendar.app.created).
   */
  listCalendars: (workspaceId: string) =>
    req<{ connections: CalendarConnection[] }>(`/workspaces/${workspaceId}/calendars`),
  connectCalendar: (workspaceId: string, label: string) =>
    req<{ url: string; grants: string }>(`/workspaces/${workspaceId}/calendars`, { method: "POST", body: { label } }),
  pageCalendars: (workspaceId: string, pageId: string) =>
    req<{ calendars: CalendarConnection[]; meet: "on" | "off" }>(`/workspaces/${workspaceId}/booking-pages/${pageId}/calendars`),
  /** `meet` left out is left alone; see bloxx-data migration 0072. */
  setPageCalendars: (workspaceId: string, pageId: string, connectionIds: string[], meet?: boolean) =>
    req<{ calendars: CalendarConnection[]; meet: "on" | "off" }>(`/workspaces/${workspaceId}/booking-pages/${pageId}/calendars`, {
      method: "PUT",
      body: { connection_ids: connectionIds, ...(meet === undefined ? {} : { meet }) },
    }),

  /** The page's own settings, notes included. See bloxx-data migration 0074. */
  bookingPage: (workspaceId: string, pageId: string) =>
    req<{ page: BookingPageNotes }>(`/workspaces/${workspaceId}/booking-pages/${pageId}`),
  /** Each key left out is left alone. `meet_room_url` must be a Google Meet room or empty. */
  setBookingPageNotes: (workspaceId: string, pageId: string, patch: { notes_enabled?: boolean; meet_room_url?: string }) =>
    req<{ page: BookingPageNotes; cancelled: number }>(`/workspaces/${workspaceId}/booking-pages/${pageId}`, {
      method: "PATCH",
      body: patch,
    }),
  /** Meeting notes, newest first. `pageId` narrows to one booking page. See bloxx-data src/meetings.ts. */
  listMeetings: (workspaceId: string, pageId?: string) =>
    req<{ sessions: MeetingSession[] }>(`/workspaces/${workspaceId}/meetings${pageId ? `?page=${encodeURIComponent(pageId)}` : ""}`),
  meeting: (workspaceId: string, sessionKey: string) =>
    req<{ session: MeetingSession; notes: MeetingNotes | null; transcript: MeetingSegment[] }>(
      `/workspaces/${workspaceId}/meetings/${encodeURIComponent(sessionKey)}`,
    ),
  /** Pull the bot out of a call. The meetings Worker decides whether partial notes follow. */
  stopMeeting: (workspaceId: string, sessionKey: string) =>
    req<{ ok: true; status: MeetingSession["status"] }>(`/workspaces/${workspaceId}/meetings/${encodeURIComponent(sessionKey)}/stop`, {
      method: "POST",
    }),
  /** Notes and transcript gone; the session stays listed as deleted. */
  deleteMeetingNotes: (workspaceId: string, sessionKey: string) =>
    req<{ ok: true }>(`/workspaces/${workspaceId}/meetings/${encodeURIComponent(sessionKey)}`, { method: "DELETE" }),

  /** A booking link from availability alone, in one step. See bloxx-data src/quickBooking.ts. */
  createBookingLink: (
    workspaceId: string,
    spec: { title: string; days: number[]; times: string[]; timezone: string; slot_minutes: number },
  ) =>
    req<{ page: { id: string; title: string }; table: { id: string; name: string }; url: string; mcp_url: string; slots_added: number }>(
      `/workspaces/${workspaceId}/booking-links`,
      { method: "POST", body: spec },
    ),

  /** A poll: a Votes table and a link, in one step. See bloxx-data src/poll.ts. */
  createPoll: (
    workspaceId: string,
    spec: { question: string; options: string[]; results?: "after_vote" | "always" | "owner"; closes_at?: number },
  ) =>
    req<{ poll: { id: string; question: string }; table: { id: string; name: string }; url: string; mcp_url: string }>(
      `/workspaces/${workspaceId}/polls`,
      { method: "POST", body: spec },
    ),

  /**
   * PDFs sent for signing (bloxx.work). See bloxx-data src/work.ts. Signers and
   * fields are each saved WHOLE: the list sent replaces the one stored, and a
   * field keeps its value only if its id comes back.
   */
  listWorkDocuments: (workspaceId: string) =>
    req<{ documents: WorkDocumentSummary[] }>(`/workspaces/${workspaceId}/work/documents`),
  getWorkDocument: (workspaceId: string, id: string) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}`),
  /** The PDF itself as the body; form fields already in it come back placed. */
  uploadWorkDocument: (workspaceId: string, file: File) =>
    req<{ document: WorkDocument; detected: { form_fields: number; skipped_form_fields: number } }>(
      `/workspaces/${workspaceId}/work/documents?title=${encodeURIComponent(file.name.replace(/\.pdf$/i, ""))}`,
      { method: "POST", rawBody: file, headers: { "Content-Type": "application/pdf" } },
    ),
  patchWorkDocument: (workspaceId: string, id: string, body: { title?: string; sender?: string; message?: string }) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}`, { method: "PATCH", body }),
  putWorkSigners: (workspaceId: string, id: string, signers: { id?: string; name: string; email?: string }[]) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}/signers`, {
      method: "PUT",
      body: { signers },
    }),
  putWorkFields: (workspaceId: string, id: string, fields: WorkFieldInput[]) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}/fields`, {
      method: "PUT",
      body: { fields },
    }),
  sendWorkDocument: (workspaceId: string, id: string) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}/send`, { method: "POST", body: {} }),
  voidWorkDocument: (workspaceId: string, id: string) =>
    req<{ document: WorkDocument }>(`/workspaces/${workspaceId}/work/documents/${id}/void`, { method: "POST", body: {} }),
  deleteWorkDocument: (workspaceId: string, id: string) =>
    req<{ deleted: true }>(`/workspaces/${workspaceId}/work/documents/${id}`, { method: "DELETE" }),
  /**
   * The bytes of a PDF, for the page renderer. Not through `req`, which reads
   * every answer as JSON. `preview` has every value so far drawn in.
   */
  workPdf: async (workspaceId: string, id: string, which: "original" | "preview" | "signed"): Promise<ArrayBuffer> => {
    const token = tokenStore.get();
    const res = await fetch(`${BASE}/workspaces/${workspaceId}/work/documents/${id}/${which}.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new DataError(res.status, `Could not load the PDF (${res.status})`);
    return res.arrayBuffer();
  },

  /** Every way this workspace is opened to somebody else. See bloxx-data src/links.ts. */
  listLinks: (workspaceId: string) => req<{ links: LinkSummary[] }>(`/workspaces/${workspaceId}/links`),
  updateLink: (workspaceId: string, kind: LinkKind, id: string, body: LinkUpdate) =>
    req<{ ok: true; revoked?: boolean }>(`/workspaces/${workspaceId}/links/${kind}/${id}`, {
      method: "PATCH",
      body,
    }),

  /**
   * Rules that tell somebody when a row changes. See bloxx-data migration 0013.
   *
   * Per TABLE rather than per workspace, because the condition is about a column:
   * "when Status becomes Done" only means anything alongside the table that has a
   * Status.
   */
  listRules: (workspaceId: string, tableId: string) =>
    req<{ rules: AutomationRule[]; kinds: string[]; ops: string[] }>(
      `/workspaces/${workspaceId}/tables/${tableId}/rules`,
    ),
  createRule: (workspaceId: string, tableId: string, body: Record<string, unknown>) =>
    req<{ rule: AutomationRule }>(`/workspaces/${workspaceId}/tables/${tableId}/rules`, { method: "POST", body }),
  patchRule: (workspaceId: string, tableId: string, id: string, body: Record<string, unknown>) =>
    req<{ rule: AutomationRule }>(`/workspaces/${workspaceId}/tables/${tableId}/rules/${id}`, {
      method: "PATCH",
      body,
    }),
  deleteRule: (workspaceId: string, tableId: string, id: string) =>
    req(`/workspaces/${workspaceId}/tables/${tableId}/rules/${id}`, { method: "DELETE" }),
  /** Fire one now, so a pasted URL can be proved before anybody depends on it. */
  testRule: (workspaceId: string, tableId: string, id: string) =>
    req<{ ok: boolean; detail: string }>(`/workspaces/${workspaceId}/tables/${tableId}/rules/${id}/test`, {
      method: "POST",
      body: {},
    }),

  /**
   * Fields filled in by asking something outside. See bloxx-data migration 0014.
   *
   * `state` comes from the workspace object rather than the definition, because
   * what somebody needs to know is per ROW — "37 done, 3 could not be reached, 200
   * came back without a price" — and a single error string on the field cannot say
   * any of that.
   */
  listDerived: (workspaceId: string, tableId: string) =>
    req<{ fields: DerivedField[]; providers: string[]; triggers: string[] }>(
      `/workspaces/${workspaceId}/tables/${tableId}/derived`,
    ),
  createDerived: (workspaceId: string, tableId: string, body: Record<string, unknown>) =>
    req<{ field: DerivedField }>(`/workspaces/${workspaceId}/tables/${tableId}/derived`, { method: "POST", body }),
  patchDerived: (workspaceId: string, tableId: string, id: string, body: Record<string, unknown>) =>
    req<{ field: DerivedField }>(`/workspaces/${workspaceId}/tables/${tableId}/derived/${id}`, {
      method: "PATCH",
      body,
    }),
  deleteDerived: (workspaceId: string, tableId: string, id: string) =>
    req(`/workspaces/${workspaceId}/tables/${tableId}/derived/${id}`, { method: "DELETE" }),
  /**
   * Try it on ONE row and hand back what the provider really said, writing
   * nothing.
   *
   * The answer to "I pasted my key and nothing happened": the raw response beside
   * what each configured path found in it. Every provider's shape is a guess until
   * somebody has seen one — the Firecrawl preset was built from documentation and
   * was wrong about both the API version and the body.
   */
  probeDerived: (workspaceId: string, tableId: string, id: string, rowId?: string) =>
    req<{
      ok: boolean;
      detail: string;
      row_id?: string;
      inputs?: Record<string, unknown>;
      resolved?: Record<string, { path: string; found: unknown }>;
      answer?: unknown;
    }>(`/workspaces/${workspaceId}/tables/${tableId}/derived/${id}/probe`, {
      method: "POST",
      body: rowId ? { row_id: rowId } : {},
    }),

  /**
   * The same, on a DRAFT that has not been saved.
   *
   * The mapping is the thing being decided, so committing it in order to find out
   * what the provider answers is the wrong way round — and it is how a preset
   * shipped with output paths nobody had ever confirmed. Validated by the same
   * rules as a save, so "it worked in the try" means something.
   */
  probeDraftDerived: (workspaceId: string, tableId: string, draft: Record<string, unknown>) =>
    req<{
      ok: boolean;
      detail: string;
      row_id?: string;
      inputs?: Record<string, unknown>;
      resolved?: Record<string, { path: string; found: unknown }>;
      answer?: unknown;
    }>(`/workspaces/${workspaceId}/tables/${tableId}/derived/probe`, { method: "POST", body: draft }),

  /** `force` ignores the unchanged-input skip — "look it up again anyway". */
  runDerived: (workspaceId: string, tableId: string, id: string, body: { row_id?: string; force?: boolean } = {}) =>
    req<{ ok: boolean; attempted: number; written: number; failed: number; detail: string }>(
      `/workspaces/${workspaceId}/tables/${tableId}/derived/${id}/run`,
      { method: "POST", body },
    ),

  /** `kind` picks which connector's links: the tables one (default) or the video one (/r/<vlk_>/mcp). */
  listTableLinks: (workspaceId: string, kind: "tables" | "video" = "tables") =>
    req<{ links: TableLink[] }>(`/workspaces/${workspaceId}/table-links${kind === "tables" ? "" : `?kind=${kind}`}`),
  mintTableLink: (workspaceId: string, label: string, canWrite: boolean, asAgent = false, kind: "tables" | "video" = "tables") =>
    req<{ link: TableLink; token: string; url: string; path: string }>(
      `/workspaces/${workspaceId}/table-links`,
      { method: "POST", body: { label, can_write: canWrite, as_agent: asAgent, ...(kind === "tables" ? {} : { kind }) } },
    ),
  revokeTableLink: (workspaceId: string, id: string) =>
    req(`/workspaces/${workspaceId}/table-links/${id}`, { method: "DELETE" }),

  addColumn: (
    workspaceId: string,
    tableId: string,
    name: string,
    type: ColumnType,
    refTable?: string,
    rollup?: { via_column_id: string; fn: RollupFn; value_column_id?: string },
    selectOptions?: SelectOption[],
  ) =>
    req<{ column: ColumnDef }>(`/workspaces/${workspaceId}/tables/${tableId}/columns`, {
      method: "POST",
      body: {
        name,
        type,
        ...(refTable ? { ref_table: refTable } : {}),
        ...(rollup ? { rollup } : {}),
        ...(selectOptions ? { select_options: selectOptions } : {}),
      },
    }),

  /**
   * Just the columns of another table.
   *
   * A rollup is built by picking a reference column on a DIFFERENT table, so the
   * form has to see schemas it is not editing. Its own method because the caller
   * wants columns and nothing else, and mapping the table read at every call site
   * is how a helper ends up written three times slightly differently.
   */
  listTableColumns: (workspaceId: string, tableId: string) =>
    req<{ table: TableDef }>(`/workspaces/${workspaceId}/tables/${tableId}`).then((r) => r.table.columns ?? []),

  /**
   * What points AT this row, grouped by the column that points.
   *
   * The read that makes a reference worth having: without it every consumer would
   * have to be told separately that a Comments table exists and which of its
   * columns points back.
   */
  /**
   * What has happened to one row, newest first.
   *
   * `retention_days` travels with it because the log is PRUNED: a history that
   * silently stops is indistinguishable from a row nobody has touched, and the
   * screen has to be able to say which.
   */
  rowHistory: (workspaceId: string, tableId: string, rowId: string) =>
    req<{
      changes: {
        id: number;
        op: string;
        actor: string;
        changed: Record<string, { from?: unknown; to?: unknown }>;
        at: number;
      }[];
      retention_days: number;
      people: Person[];
    }>(`/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}/history`),

  /**
   * Point another table's row AT this one, or stop it pointing.
   *
   * A backlink is not a thing that can be edited where it is shown: it is a
   * `ref` cell on the OTHER row, so "add an associated item" is a write to that
   * table and "remove" is clearing that cell. Doing it from here is the whole
   * difference between a panel you read and a relationship you manage.
   */
  setRef: (workspaceId: string, tableId: string, rowId: string, columnName: string, value: string | null) =>
    req<{ row: RowOut }>(`/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}`, {
      method: "PATCH",
      body: { fields: { [columnName]: value } },
    }),

  /**
   * Upload one file and get back the descriptor to put in a cell.
   *
   * Streams the File straight up as the request body rather than wrapping it in
   * multipart: there is exactly one file, and multipart would mean buffering it
   * to build a boundary for no gain.
   */
  uploadFile: async (workspaceId: string, tableId: string, file: File) =>
    req<{ file: FileRef; url: string }>(`/workspaces/${workspaceId}/tables/${tableId}/files`, {
      method: "POST",
      rawBody: file,
      headers: { "Content-Type": file.type || "application/octet-stream", "X-File-Name": file.name },
    }),

  /** The thread on one row. Separate from history: comments are not pruned. */
  comments: (workspaceId: string, tableId: string, rowId: string) =>
    req<{ comments: RowComment[]; people: Person[]; me: string }>(
      `/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}/comments`,
    ),
  addComment: (workspaceId: string, tableId: string, rowId: string, body: string) =>
    req<{ comment: RowComment }>(`/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}/comments`, {
      method: "POST",
      body: { body },
    }),
  deleteComment: (workspaceId: string, tableId: string, rowId: string, commentId: string) =>
    req<{ ok: true }>(
      `/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}/comments/${commentId}`,
      { method: "DELETE" },
    ),

  backlinks: (workspaceId: string, tableId: string, rowId: string) =>
    req<{ groups: BacklinkGroup[]; people: Person[] }>(
      `/workspaces/${workspaceId}/tables/${tableId}/rows/${rowId}/backlinks`,
    ),

  /**
   * Insert up to 500 rows in one request.
   *
   * NOT ATOMIC, and that is the point: `failed` names the rows the server would
   * not take, and the rest are in. A 900-row CSV with one unparseable cell should
   * import 899 rows and say which one it dropped.
   */
  insertRows: (workspaceId: string, tableId: string, rows: { fields: Record<string, unknown> }[]) =>
    req<{ inserted: number; failed: { index: number; message: string }[] }>(
      `/workspaces/${workspaceId}/tables/${tableId}/rows/bulk`,
      { method: "POST", body: { rows } },
    ),

  listViews: (workspaceId: string, tableId: string) =>
    req<{ views: ViewRecord[] }>(`/workspaces/${workspaceId}/tables/${tableId}/views`),
  createView: (workspaceId: string, tableId: string, body: Record<string, unknown>) =>
    req<{ view: ViewRecord }>(`/workspaces/${workspaceId}/tables/${tableId}/views`, { method: "POST", body }),
  updateView: (workspaceId: string, tableId: string, viewId: string, body: Record<string, unknown>) =>
    req<{ view: ViewRecord }>(`/workspaces/${workspaceId}/tables/${tableId}/views/${viewId}`, {
      method: "PATCH",
      body,
    }),
  deleteView: (workspaceId: string, tableId: string, viewId: string) =>
    req(`/workspaces/${workspaceId}/tables/${tableId}/views/${viewId}`, { method: "DELETE" }),

  /** The public URL a `public` view is served at. Scoped views deliberately have none. */
  publicViewUrl: (viewId: string) => `${BASE}/v/${viewId}`,

  /**
   * Publishing a table.
   *
   * A "feed" IS a published table — the same object with a publication row
   * attached, which is why this lives here beside the schema rather than behind a
   * separate noun. The admin app used to present the two as different sections
   * with different words for the same tree, and the model never agreed.
   */
  /**
   * The name a workspace's tables are published under. A provider IS a workspace,
   * so this is one per workspace and created on first publish rather than being a
   * separate thing to set up first.
   */
  listProviders: (workspaceId: string) =>
    req<{ providers: { id: string; slug: string; name: string }[] }>(`/workspaces/${workspaceId}/providers`),
  createProvider: (workspaceId: string, name: string, slug: string) =>
    req<{ provider: { id: string; slug: string; name: string } }>(`/workspaces/${workspaceId}/providers`, {
      method: "POST",
      body: { name, slug },
    }),

  getPublication: (workspaceId: string, tableId: string) =>
    req<{ publication: PublicationRecord | null }>(`/workspaces/${workspaceId}/tables/${tableId}/publication`),
  savePublication: (workspaceId: string, tableId: string, body: Record<string, unknown>) =>
    req<{ publication: PublicationRecord }>(`/workspaces/${workspaceId}/tables/${tableId}/publication`, {
      method: "PUT",
      body,
    }),
  unpublish: (workspaceId: string, tableId: string) =>
    req(`/workspaces/${workspaceId}/tables/${tableId}/publication`, { method: "DELETE" }),

  listTokens: () => req<{ tokens: TokenRecord[] }>("/tokens"),
  mintToken: (input: MintTokenInput) =>
    req<{ token: string; note: string; token_info: TokenRecord }>("/tokens", { method: "POST", body: input }),
  revokeToken: (id: string) => req(`/tokens/${id}`, { method: "DELETE" }),

  /** The one question a Durable Object structurally cannot answer about itself. */
  search: (q: string, type?: ItemType) => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (type) p.set("type", type);
    return req<{ hits: SearchHit[]; total: number }>(`/search?${p}`);
  },

  /**
   * A site's business email, through the Worker's /email/:siteId proxy to
   * bloxx-mail. The Worker resolves the domain from the site and checks the
   * caller is its owner, admin or editor; nothing here names a domain.
   * Refusals arrive as DataError with the structured body: read them with
   * `emailRefusal` from `@/lib/email`.
   */
  email: {
    status: (siteId: number) => req<EmailStatus>(`/email/${siteId}`),
    setup: (siteId: number, input: EmailSetupInput) =>
      req<EmailSetupResult | EmailSetupPreview>(`/email/${siteId}/setup`, { method: "POST", body: input }),
    /** `replace` overwrites an address that exists; without it bloxx-mail answers 409 `exists`. */
    addAddress: (siteId: number, localPart: string, copyTo: string | null, replace = false) =>
      req<{ ok: true; address: string; copy_to: string | null }>(`/email/${siteId}/addresses`, {
        method: "POST",
        body: replace ? { local_part: localPart, copy_to: copyTo, replace: true } : { local_part: localPart, copy_to: copyTo },
      }),
    removeAddress: (siteId: number, localPart: string) =>
      req<{ ok: true }>(`/email/${siteId}/addresses/${encodeURIComponent(localPart)}`, { method: "DELETE" }),
    test: (siteId: number, send: boolean) => req<EmailTestResult>(`/email/${siteId}/test`, { method: "POST", body: { send } }),
    log: (siteId: number) => req<EmailLogEntry[]>(`/email/${siteId}/log`),
    /** Recent form leads on the domain, newest first, with the replies sent to each. */
    leads: (siteId: number, limit = 50) => req<{ leads: EmailLead[] }>(`/email/${siteId}/leads?limit=${limit}`),
    /** Answers a lead from the business address. 409 `no_email` / `sending_off`, 502 `send_failed`. */
    reply: (siteId: number, submissionId: number, text: string) =>
      req<EmailReplyResult>(`/email/${siteId}/leads/${submissionId}/reply`, { method: "POST", body: { text } }),
    hooks: (siteId: number) => req<{ hooks: EmailLeadHook[] }>(`/email/${siteId}/hooks`),
    /** The answer carries the signing secret, once. Nothing else ever returns it. */
    addHook: (siteId: number, url: string, label?: string) =>
      req<EmailLeadHookCreated>(`/email/${siteId}/hooks`, { method: "POST", body: label ? { url, label } : { url } }),
    removeHook: (siteId: number, hookId: string) =>
      req<{ ok: true }>(`/email/${siteId}/hooks/${encodeURIComponent(hookId)}`, { method: "DELETE" }),
    testHook: (siteId: number, hookId: string) =>
      req<EmailLeadHookTestResult>(`/email/${siteId}/hooks/${encodeURIComponent(hookId)}/test`, { method: "POST" }),
    deliveries: (siteId: number, hookId: string, limit = 50) =>
      req<{ deliveries: EmailLeadDelivery[] }>(`/email/${siteId}/hooks/${encodeURIComponent(hookId)}/deliveries?limit=${limit}`),
  },
};

/** Link an explicitly reviewed existing release. This never publishes it. */
export function reviewVisibilityWebflowRelease(input:{project:string;opportunityId:string;confirmationToken:string;qualityApproved:true}) {
  return req<{previewId:string;sourceRevision:string;fingerprint:string;reviewUrl:string}>('/visibility/destination/review',{method:'POST',body:input});
}


/** The native draft document exactly as the review screen reads and edits it. Structural on purpose:
 * the admin SPA does not compile against Worker source, and fields it does not touch round-trip untouched. */
type NativeEntity={id:string;schemaVersion:1;revision:number};
export type NativeDocument={
 schemaVersion:1;
 site:NativeEntity&{name:string;homePageId:string;navigation:{pageId:string;label:string}[];sharedSectionIds:string[]};
 pages:(NativeEntity&{siteId:string;parentPageId:string|null;slug:string;title:string;purpose:string;sectionIds:string[];seo:{description:string;canonical:string;index:boolean}})[];
 sections:(NativeEntity&{siteId:string;pageId:string|null;componentRef:{key:string;version:string};props:Record<string,unknown>;bindings:unknown[]})[];
 collections:(NativeEntity&{siteId:string}&Record<string,unknown>)[];
 items:(NativeEntity&{collectionId:string;fields:Record<string,unknown>}&Record<string,unknown>)[];
};
export type NativeMutationRequest<T>={schemaVersion:1;siteId:string;mode:'preview'|'commit';expectedSiteRevision:number;expectedEntityRevisions:Record<string,number>;idempotencyKey:string;input:T;confirmationToken?:string};
export const nativeVisibilityReview = {
  draft: (siteId:string) => req<{siteId:string;revision:number;document:NativeDocument}>(`/native-sites/${encodeURIComponent(siteId)}/draft`),
  opportunities: (project:string) => req<Array<{id:string;contentRef:{kind:string;pageId?:string}}>>('/visibility/opportunities',{method:'POST',body:{project}}),
  capabilities: (siteId:string,pageId:string) => req<{canStage:boolean}>('/visibility/destination/capabilities',{method:'POST',body:{content:{kind:'native_page',siteId,pageId}}}),
  preview: (input:NativeMutationRequest<NativeDocument>) => req<{proposalId:string;proposalHash:string;confirmationToken:string;expiresAt:number;expectedSiteRevision:number}>(`/native-sites/${encodeURIComponent(input.siteId)}/draft`,{method:'POST',body:input}),
  confirm: (input:{project:string;opportunityId:string;proposalId:string;confirmationToken:string;qualityApproved:true}) => req<{previewId:string;revision:number}>('/visibility/destination/review',{method:'POST',body:input}),
};

export interface VisibilityWebflowReview {
 proposalId:string; sourceRevision:string; fingerprint:string; expiresAt:string; reviewUrl:string;
 changes:{contentIdentity:string;before:Record<string,unknown>|null;after:Record<string,unknown>}[];
 review:{screenshots:{contentIdentity:string;viewport:string;url:string;hash:string}[];publishLane:string;verificationTargets?:{url:string;contentIdentity:string}[]};
 quality:{findings:{message:string;severity:string;remediation:string}[]};
}
export const visibilityWebflowReviews={
 list:(project:string,opportunityId:string)=>req<{proposals:{id:string;createdAt:string;expiresAt:string;publishLane:string}[]}>('/visibility/destination/review',{method:'POST',body:{project,opportunityId,mode:'list'}}),
 inspect:(project:string,opportunityId:string,proposalId:string)=>req<VisibilityWebflowReview>('/visibility/destination/review',{method:'POST',body:{project,opportunityId,proposalId,mode:'inspect'}}),
 link:(project:string,opportunityId:string,review:VisibilityWebflowReview)=>req<{previewId:string;sourceRevision:string;fingerprint:string;reviewUrl:string}>('/visibility/destination/review',{method:'POST',body:{project,opportunityId,proposalId:review.proposalId,sourceRevision:review.sourceRevision,fingerprint:review.fingerprint,qualityApproved:true}}),
};

export interface VisibilityXanoDraftReview {
  sourceRevision:string; fingerprint:string; exactDiff:string; publicUrl:string; reviewUrl:string; canLink:boolean;
}
export const visibilityXanoDraftReviews = {
  inspect:(project:string,opportunityId:string)=>req<VisibilityXanoDraftReview>('/visibility/destination/current',{method:'POST',body:{project,opportunityId}}),
  link:(project:string,opportunityId:string,review:VisibilityXanoDraftReview)=>req<{previewId:string;sourceRevision:string;fingerprint:string}>('/visibility/destination/review',{method:'POST',body:{project,opportunityId,sourceRevision:review.sourceRevision,fingerprint:review.fingerprint,qualityApproved:true}}),
};

export type VisibilityDataReview=VisibilityXanoDraftReview & {publicationState:'draft'|'currently_live';basis:string};
export const visibilityDataReviews={
 inspect:(project:string,opportunityId:string)=>req<VisibilityDataReview>('/visibility/destination/current',{method:'POST',body:{project,opportunityId}}),
 link:(project:string,opportunityId:string,review:VisibilityDataReview)=>req<{previewId:string;sourceRevision:string;fingerprint:string}>('/visibility/destination/review',{method:'POST',body:{project,opportunityId,sourceRevision:review.sourceRevision,fingerprint:review.fingerprint,qualityApproved:true}}),
};
export type VisibilityDataRestorePreview={previewId:string;confirmationToken:string;fingerprint:string;exactDiff:string;missing:string[];publicationPreserved:true};
export const visibilityDataRestores={
 list:(project:string,opportunityId:string)=>req<{revisions:{id:string;reason:string;created_at:number}[]}>('/visibility/destination/restore/list',{method:'POST',body:{project,opportunityId}}),
 preview:(project:string,opportunityId:string,revisionId:string)=>req<VisibilityDataRestorePreview>('/visibility/destination/restore/preview',{method:'POST',body:{project,opportunityId,revisionId}}),
 apply:(project:string,opportunityId:string,preview:VisibilityDataRestorePreview)=>req<{ok:boolean;sections_missing:string[]}>('/visibility/destination/restore/apply',{method:'POST',body:{project,opportunityId,previewId:preview.previewId,confirmationToken:preview.confirmationToken,fingerprint:preview.fingerprint,qualityApproved:true}}),
};

// ── Studio (src/studio.ts on the Worker) ─────────────────────────────────────
// Its own export rather than more methods on `bloxxData`, the same call the
// visibility clients above make: a module with its own vocabulary reads better
// as its own object.

const studioBase = (workspaceId: string) => `/workspaces/${workspaceId}/studio/recordings`;

/** Captures: what somebody did, kept. One row read as feedback or as a guide. */
export interface CaptureSummary {
  id: string;
  kind: "feedback" | "guide";
  title: string;
  note: string;
  steps: number;
  trouble: number;
  shots: number;
  shared: boolean;
  created_by: number;
  created_at: number;
}

export interface CaptureStep {
  at: number;
  /** `said` came from the narration: words and a moment, no control to point at. */
  kind: "click" | "field" | "route" | "error" | "request" | "said";
  route: string;
  target?: { tag: string; role: string | null; name: string; selector: string };
  detail?: string;
  /** The person's own words for this step, which win over the derived line. */
  text?: string;
  /** The still's id, when one is stored. The bytes come from `shotBlob`. */
  shot?: string;
}

export interface CaptureDetail {
  id: string;
  kind: "feedback" | "guide";
  title: string;
  note: string;
  where: { href?: string; build?: string | null; viewport?: string; userAgent?: string };
  steps: CaptureStep[];
  shots: number;
  retrospective: boolean;
  shared: boolean;
  /** The link, when it is shared. Null otherwise. */
  share_link: string | null;
  created_by: number;
  created_at: number;
  updated_at: number;
}

export const capturesApi = {
  list: (workspaceId: string, kind?: "feedback" | "guide") =>
    req<{ captures: CaptureSummary[] }>(`/workspaces/${workspaceId}/captures${kind ? `?kind=${kind}` : ""}`),
  get: (workspaceId: string, id: string) =>
    req<{ capture: CaptureDetail }>(`/workspaces/${workspaceId}/captures/${id}`),
  /**
   * A step's still, as bytes.
   *
   * NOT AN <img src>. The route is authenticated and an image element cannot
   * carry a bearer token, so the browser would ask for it without one and get a
   * 404. Studio solves the same problem with a token in the media URL; here the
   * stills are few and private, so fetching them and holding object URLs is
   * simpler than minting a second credential.
   */
  shotBlob: async (workspaceId: string, id: string, shot: string): Promise<Blob> => {
    const token = tokenStore.get();
    const res = await fetch(`${BASE}/workspaces/${workspaceId}/captures/${id}/shots/${shot}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`that screenshot could not be loaded (${res.status})`);
    return res.blob();
  },
  create: (workspaceId: string, body: Record<string, unknown>) =>
    req<{ capture: { id: string } }>(`/workspaces/${workspaceId}/captures`, { method: "POST", body }),
  /** One still, as the JPEG itself. The step must already say it has one. */
  shot: (workspaceId: string, id: string, shot: string, bytes: Blob) =>
    req<{ stored: string; shots: number }>(`/workspaces/${workspaceId}/captures/${id}/shots/${shot}`, {
      method: "POST",
      rawBody: bytes,
      headers: { "Content-Type": "image/jpeg" },
    }),
  patch: (workspaceId: string, id: string, body: Record<string, unknown>) =>
    req<{ capture: CaptureDetail }>(`/workspaces/${workspaceId}/captures/${id}`, { method: "PATCH", body }),
  remove: (workspaceId: string, id: string) =>
    req<{ deleted: true }>(`/workspaces/${workspaceId}/captures/${id}`, { method: "DELETE" }),
  /** Share it. Turning sharing off and on again mints a NEW link, never the old one. */
  share: (workspaceId: string, id: string) =>
    req<{ share_token: string; link: string }>(`/workspaces/${workspaceId}/captures/${id}/share`, { method: "POST" }),
  unshare: (workspaceId: string, id: string) =>
    req<{ share_token: null; link: null }>(`/workspaces/${workspaceId}/captures/${id}/share`, { method: "DELETE" }),
};

export const studioApi = {
  list: (workspaceId: string) => req<{ recordings: StudioRecording[] }>(studioBase(workspaceId)),
  /**
   * Keep a recording made on bloxx.video without an account: it moves into this
   * workspace and stops expiring. The share link is unchanged, so one already
   * sent to somebody keeps working.
   */
  claim: (workspaceId: string, body: { recording: string; token: string }) =>
    req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/claim`, { method: "POST", body }),
  get: (workspaceId: string, id: string) =>
    req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}`),
  create: (
    workspaceId: string,
    body: { title: string; content_type: string; source: StudioSource; bytes: number },
  ) =>
    req<{ recording: StudioRecording; part_bytes: number }>(studioBase(workspaceId), { method: "POST", body }),
  uploadPart: (workspaceId: string, id: string, partNumber: number, bytes: Blob) =>
    req<{ part_number: number; etag: string }>(`${studioBase(workspaceId)}/${id}/parts/${partNumber}`, {
      method: "POST",
      rawBody: bytes,
      headers: { "Content-Type": "application/octet-stream" },
    }),
  complete: (
    workspaceId: string,
    id: string,
    body: { parts: { part_number: number; etag: string }[]; duration_s: number; width: number; height: number },
  ) => req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}/complete`, { method: "POST", body }),
  transcribe: (workspaceId: string, id: string, body: { audio: string; offset_s: number }) =>
    req<StudioTranscript>(`${studioBase(workspaceId)}/${id}/transcribe`, { method: "POST", body }),
  patch: (
    workspaceId: string,
    id: string,
    body: { title?: string; transcript?: StudioTranscript | null; edit?: StudioEdit | null; public?: boolean },
  ) => req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}`, { method: "PATCH", body }),
  remove: (workspaceId: string, id: string) =>
    req<{ deleted: true }>(`${studioBase(workspaceId)}/${id}`, { method: "DELETE" }),
  /**
   * Caption lines in another language, timed to this edit. `pending` means the
   * translation is being made; ask again. The same stored translation the share
   * page uses, so reading a recording in Portuguese and exporting it in
   * Portuguese cost one generation between them.
   */
  captions: (workspaceId: string, id: string, lang: string | null) =>
    req<{ status: "ready" | "pending" | "failed"; lang: string | null; error?: string; lines: Caption[] }>(
      `${studioBase(workspaceId)}/${id}/captions${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`,
    ),
  /**
   * What a prompt would do to `edit`: a proposed edit and its diff. Nothing is
   * saved; applying the proposal is an ordinary `patch` of the edit.
   */
  promptEdit: (workspaceId: string, id: string, prompt: string, edit: StudioEdit) =>
    req<StudioPromptEditResult>(`${studioBase(workspaceId)}/${id}/prompt-edit`, { method: "POST", body: { prompt, edit } }),
  /** The camera file of a "Layout later" screen recording: open, parts, complete. */
  startCamera: (workspaceId: string, id: string, body: { content_type: string; bytes: number }) =>
    req<{ recording: StudioRecordingDetail; part_bytes: number }>(`${studioBase(workspaceId)}/${id}/camera`, { method: "POST", body }),
  uploadCameraPart: (workspaceId: string, id: string, partNumber: number, bytes: Blob) =>
    req<{ part_number: number; etag: string }>(`${studioBase(workspaceId)}/${id}/camera/parts/${partNumber}`, {
      method: "POST",
      rawBody: bytes,
      headers: { "Content-Type": "application/octet-stream" },
    }),
  completeCamera: (
    workspaceId: string,
    id: string,
    body: { parts: { part_number: number; etag: string }[]; offset_s: number; width: number; height: number },
  ) => req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}/camera/complete`, { method: "POST", body }),
  share: (workspaceId: string, id: string, shared: boolean, poster?: string | null) =>
    req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}/share`, {
      method: "POST",
      body: { shared, poster: poster ?? undefined },
    }),
  /** Who has been crawling a public recording: AI, search, and how many read the words. */
  crawls: (workspaceId: string, id: string, days = 30) =>
    req<StudioCrawlSummary>(`${studioBase(workspaceId)}/${id}/crawls?days=${days}`),
  /**
   * The code to paste into a website, built by the Worker so the dialog, the
   * connector and oEmbed describe a video the same way. `html` is an iframe with
   * a fallback link (and, when public, the video's schema); `element` is the
   * <bloxx-video> form.
   */
  embedCode: (workspaceId: string, id: string, transcript: boolean) =>
    req<{ public: boolean; html: string; element: string; head: string; markdown: string; react: string }>(
      `${studioBase(workspaceId)}/${id}/embed-code${transcript ? "?transcript=1" : ""}`,
    ),
  /**
   * Replace the still WITHOUT touching the share link: the only way to change a
   * thumbnail that does not retire the link people were already sent.
   */
  setPoster: (workspaceId: string, id: string, poster: string) =>
    req<{ recording: StudioRecordingDetail }>(`${studioBase(workspaceId)}/${id}/poster`, { method: "POST", body: { poster } }),
  /** `media_url` is a path on the data API; this makes it something a <video> can load. */
  mediaUrl: (path: string) => `${BASE}${path}`,
};

// ── Media (src/media.ts on the Worker) ───────────────────────────────────────

const mediaBase = (workspaceId: string) => `/workspaces/${workspaceId}/media`;

export interface BrandSuggestion {
  name: string;
  domain: string;
  kit: BrandKit;
  found: {
    colors: string[];
    fonts: string[];
    icons: string[];
    og_image: string;
    description: string;
    /** The site's second brand colour, also in kit.colors.secondary. */
    secondary?: string | null;
    /** Where each logo candidate tried came from, in order. */
    logo_candidates?: string[];
    stylesheets_read?: number;
    /** Stylesheets and logo files that could not be used, and why. */
    skipped?: { url: string; reason: string }[];
  };
}

export interface NewAssetMeta {
  kind: MediaAssetKind;
  content_type: string;
  brand_id?: string | null;
  template?: string;
  format?: string;
  title?: string;
  batch_id?: string;
  position?: number;
  width?: number;
  height?: number;
  params?: unknown;
}

export const mediaApi = {
  brands: (workspaceId: string) => req<{ brands: MediaBrand[] }>(`${mediaBase(workspaceId)}/brands`),
  brand: (workspaceId: string, id: string) => req<{ brand: MediaBrand }>(`${mediaBase(workspaceId)}/brands/${id}`),
  importBrand: (workspaceId: string, url: string) =>
    req<{ suggestion: BrandSuggestion }>(`${mediaBase(workspaceId)}/brands/import`, { method: "POST", body: { url } }),
  /** `warnings` says when an imported logo could not be stored; the brand is saved without it. */
  createBrand: (workspaceId: string, body: { name: string; domain: string; kit: BrandKit }) =>
    req<{ brand: MediaBrand; warnings?: string[] }>(`${mediaBase(workspaceId)}/brands`, { method: "POST", body }),
  patchBrand: (workspaceId: string, id: string, body: { name?: string; domain?: string; kit?: BrandKit }) =>
    req<{ brand: MediaBrand; warnings?: string[] }>(`${mediaBase(workspaceId)}/brands/${id}`, { method: "PATCH", body }),
  removeBrand: (workspaceId: string, id: string) =>
    req<{ deleted: true }>(`${mediaBase(workspaceId)}/brands/${id}`, { method: "DELETE" }),
  assets: (workspaceId: string, filter: { brand_id?: string; kind?: string; batch_id?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]).toString();
    return req<{ assets: MediaAsset[] }>(`${mediaBase(workspaceId)}/assets${q ? `?${q}` : ""}`);
  },
  /** `image` is the file as base64 without a data: prefix. */
  uploadAsset: (workspaceId: string, image: string, meta: NewAssetMeta) =>
    req<{ asset: MediaAsset }>(`${mediaBase(workspaceId)}/assets`, { method: "POST", body: { image, ...meta } }),
  removeAsset: (workspaceId: string, id: string) =>
    req<{ deleted: true }>(`${mediaBase(workspaceId)}/assets/${id}`, { method: "DELETE" }),
  generateImage: (
    workspaceId: string,
    body: { prompt: string; size: "square" | "landscape" | "portrait" | "story"; style?: "photo" | "illustration"; brand_id?: string; batch_id?: string },
  ) => req<{ asset: MediaAsset }>(`${mediaBase(workspaceId)}/generate/image`, { method: "POST", body }),
  generateCarousel: (workspaceId: string, body: { source_url?: string; source_text?: string; brand_id?: string; slides?: number }) =>
    req<{ format: "carousel"; copy: CarouselCopy; source_url: string; unverified: string[] }>(`${mediaBase(workspaceId)}/generate/copy`, {
      method: "POST",
      body: { ...body, format: "carousel" },
    }),
  generateAd: (workspaceId: string, body: { source_url?: string; source_text?: string; brand_id?: string }) =>
    req<{ format: "ad"; copy: AdCopy; source_url: string; unverified: string[] }>(`${mediaBase(workspaceId)}/generate/copy`, {
      method: "POST",
      body: { ...body, format: "ad" },
    }),
  /** An asset's `url` is a path on the data API; this makes it loadable. */
  fileUrl: (path: string) => `${BASE}${path}`,
};

export interface ReplayRecordingConfig {
  siteId: string; version: number; recordingEnabled: boolean;
  samplingRate: number; usedBytes: number; dailyBudgetBytes: number;
  activationBlockers: string[];
}
export const replayRecording = {
  list: (siteId: number) => req<{sessions:Array<{sessionId:string;startedAt:number;lastActivityAt:number;status:string;receivedBytes:number;gaps:string[]}>}>(`/replay/sites/${siteId}/sessions`),
  read: (siteId: number) => req<ReplayRecordingConfig>(`/replay/sites/${siteId}/config`),
  update: (siteId: number, expectedVersion: number, recordingEnabled: boolean) =>
    req<ReplayRecordingConfig>(`/replay/sites/${siteId}/config`, {
      method: "PATCH", body: { expectedVersion, recordingEnabled },
    }),
};

/** Recordings by website (src/replay/query/website.ts, website-replay/1): every recording key the website has had,
 *  each inside its interval, distinct sessions counted once. Members of the site read it; anyone else gets 404. */
export const replayWebsite = {
  sessions: (websiteId: number, from: string, to: string) =>
    req<unknown>(`/replay/websites/${websiteId}/sessions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
};

export function replayOperation<T>(operation: string, args: Record<string, unknown>): Promise<T> {
  return req<T>('/replay/query', {method:'POST',body:{operation,arguments:args}});
}

export const replayInstallation={
 register:(siteId:string,origin:string)=>req<import('../features/replay-installation/ReplayInstallation').ReplayInstallationDetails>(`/replay/sites/${encodeURIComponent(siteId)}/installations`,{method:'POST',body:{origin}}),
 verify:(siteId:string,id:string)=>req<import('../features/replay-installation/ReplayInstallation').ReplayInstallationDetails>(`/replay/sites/${encodeURIComponent(siteId)}/installations/${encodeURIComponent(id)}/verify`,{method:'POST',body:{}}),
};
